import { z } from "zod/v3";
import { NODE_TYPES, type NodeTypeKey } from "@/config/nodes";
import type { WorkflowDef, PipelineNodeDef } from "@/config/pipeline";
import type { Edge, MarkerType } from "reactflow";

// Inline MarkerType.ArrowClosed to avoid importing reactflow runtime in server bundles
const MARKER_ARROW_CLOSED = "arrowclosed" as unknown as MarkerType;

// ── Zod Schemas ──────────────────────────────────────────────────────────────

const PositionSchema = z.object({
  x: z.number(),
  y: z.number(),
});

const TechParamSchema = z.object({
  label: z.string(),
  value: z.string(),
});

const PipelineNodeDefSchema = z.object({
  id: z.string().min(1),
  label: z.string(),
  service: z.string(),
  icon: z.string(),
  inputLabel: z.string(),
  outputLabel: z.string(),
  command: z.string().nullable(),
  position: PositionSchema,
  description: z.string(),
  isFirst: z.boolean().optional(),
  isLast: z.boolean().optional(),
  isGate: z.boolean().optional(),
  model: z.string().optional(),
  prompt: z.string().optional(),
  techParams: z.array(TechParamSchema).optional(),
});

const EdgeSchema = z.object({
  id: z.string(),
  source: z.string(),
  target: z.string(),
  type: z.string().optional(),
  animated: z.boolean().optional(),
  label: z.union([z.string(), z.any()]).optional(),
  labelBgPadding: z.any().optional(),
  labelBgBorderRadius: z.any().optional(),
  labelBgStyle: z.any().optional(),
  labelStyle: z.any().optional(),
  style: z.any().optional(),
  markerEnd: z.any().optional(),
});

export const WorkflowDefSchema = z.object({
  id: z.string(),
  name: z.string(),
  tagline: z.string().optional(),
  description: z.string(),
  category: z.enum(["Research and analysis", "Content Creation", "Marketing"]),
  icon: z.string(),
  brandLogos: z.array(z.string()).optional(),
  outputs: z.array(z.string()).optional(),
  nodes: z.array(PipelineNodeDefSchema),
  edges: z.array(EdgeSchema),
});

// ── Pure Mutation Functions ──────────────────────────────────────────────────

export function cloneWorkflow(wf: WorkflowDef): WorkflowDef {
  return JSON.parse(JSON.stringify(wf));
}

function makeEdge(
  id: string,
  source: string,
  target: string,
  label: string,
  color = "#8b5cf6",
): Edge {
  return {
    id,
    source,
    target,
    type: "smoothstep",
    animated: true,
    label,
    labelBgPadding: [8, 4] as [number, number],
    labelBgBorderRadius: 4,
    labelBgStyle: { fill: "#1a1b1e", color: "#fff" },
    labelStyle: { fill: color, fontWeight: 600, fontSize: 10 },
    style: { stroke: color, strokeWidth: 2 },
    markerEnd: { type: MARKER_ARROW_CLOSED, color },
  };
}

export function applyNodeUpdate(
  wf: WorkflowDef,
  nodeId: string,
  updates: Partial<
    Pick<PipelineNodeDef, "label" | "description" | "model" | "prompt" | "techParams">
  >,
): WorkflowDef {
  const clone = cloneWorkflow(wf);
  const node = clone.nodes.find((n) => n.id === nodeId);
  if (!node) throw new Error(`Node ${nodeId} not found`);
  Object.assign(node, updates);
  return clone;
}

export function applyAddNode(
  wf: WorkflowDef,
  nodeTypeKey: string,
  nodeId: string,
  position: { x: number; y: number },
  edgeDefs?: Array<{ source: string; target: string; label: string }>,
): WorkflowDef {
  const nodeType = NODE_TYPES[nodeTypeKey as NodeTypeKey];
  if (!nodeType) throw new Error(`Unknown node type: ${nodeTypeKey}`);

  const clone = cloneWorkflow(wf);

  // Check for duplicate ID
  if (clone.nodes.some((n) => n.id === nodeId)) {
    throw new Error(`Node ID ${nodeId} already exists`);
  }

  const newNode: PipelineNodeDef = {
    ...nodeType,
    id: nodeId,
    position,
  };

  clone.nodes.push(newNode);

  // Add edges if specified
  if (edgeDefs) {
    for (const def of edgeDefs) {
      clone.edges.push(makeEdge(`e${def.source}-${def.target}`, def.source, def.target, def.label));
    }
  }

  return recalculateBoundaries(clone);
}

export function applyRemoveNode(wf: WorkflowDef, nodeId: string): WorkflowDef {
  const clone = cloneWorkflow(wf);
  const idx = clone.nodes.findIndex((n) => n.id === nodeId);
  if (idx === -1) throw new Error(`Node ${nodeId} not found`);

  clone.nodes.splice(idx, 1);
  clone.edges = clone.edges.filter((e) => e.source !== nodeId && e.target !== nodeId);

  return recalculateBoundaries(clone);
}

export function applyReorderNodes(
  wf: WorkflowDef,
  updates: Array<{ nodeId: string; position: { x: number; y: number } }>,
  newEdges?: Array<{ source: string; target: string; label: string }>,
): WorkflowDef {
  const clone = cloneWorkflow(wf);

  for (const u of updates) {
    const node = clone.nodes.find((n) => n.id === u.nodeId);
    if (node) node.position = u.position;
  }

  if (newEdges) {
    clone.edges = newEdges.map((e) =>
      makeEdge(`e${e.source}-${e.target}`, e.source, e.target, e.label),
    );
  }

  return recalculateBoundaries(clone);
}

function recalculateBoundaries(wf: WorkflowDef): WorkflowDef {
  const nodeIds = new Set(wf.nodes.map((n) => n.id));
  const hasIncoming = new Set<string>();
  const hasOutgoing = new Set<string>();

  for (const edge of wf.edges) {
    if (nodeIds.has(edge.source)) hasOutgoing.add(edge.source);
    if (nodeIds.has(edge.target)) hasIncoming.add(edge.target);
  }

  for (const node of wf.nodes) {
    node.isFirst = !hasIncoming.has(node.id);
    node.isLast = !hasOutgoing.has(node.id);
  }

  return wf;
}

// ── Validation ───────────────────────────────────────────────────────────────

export function validateWorkflowMutation(wf: WorkflowDef): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  // Zod structural validation
  const result = WorkflowDefSchema.safeParse(wf);
  if (!result.success) {
    errors.push(...result.error.issues.map((i) => `${i.path.join(".")}: ${i.message}`));
    return { valid: false, errors };
  }

  const nodeIds = new Set(wf.nodes.map((n) => n.id));

  // Check for duplicate IDs
  if (nodeIds.size !== wf.nodes.length) {
    errors.push("Duplicate node IDs detected");
  }

  // Check all edge references exist
  for (const edge of wf.edges) {
    if (!nodeIds.has(edge.source))
      errors.push(`Edge ${edge.id}: source node ${edge.source} not found`);
    if (!nodeIds.has(edge.target))
      errors.push(`Edge ${edge.id}: target node ${edge.target} not found`);
  }

  // Check for circular edges (self-loops)
  for (const edge of wf.edges) {
    if (edge.source === edge.target)
      errors.push(`Edge ${edge.id}: self-loop on node ${edge.source}`);
  }

  // Simple cycle detection via DFS
  const adjacency = new Map<string, string[]>();
  for (const node of wf.nodes) adjacency.set(node.id, []);
  for (const edge of wf.edges) {
    adjacency.get(edge.source)?.push(edge.target);
  }

  const visited = new Set<string>();
  const inStack = new Set<string>();

  function hasCycle(nodeId: string): boolean {
    visited.add(nodeId);
    inStack.add(nodeId);
    for (const neighbor of adjacency.get(nodeId) || []) {
      if (!visited.has(neighbor)) {
        if (hasCycle(neighbor)) return true;
      } else if (inStack.has(neighbor)) {
        return true;
      }
    }
    inStack.delete(nodeId);
    return false;
  }

  for (const nodeId of nodeIds) {
    if (!visited.has(nodeId) && hasCycle(nodeId)) {
      errors.push("Circular dependency detected in workflow graph");
      break;
    }
  }

  // Validate commands against known set
  const validCommands = new Set(
    Object.values(NODE_TYPES)
      .map((n) => n.command)
      .filter(Boolean),
  );
  for (const node of wf.nodes) {
    if (node.command !== null && !validCommands.has(node.command)) {
      errors.push(`Node ${node.id}: unknown command '${node.command}'`);
    }
  }

  return { valid: errors.length === 0, errors };
}
